import { BaseElement, Editor, Element, Node, Path, PathRef, Range, Text, TextUnit, Transforms } from 'slate'

import {
  CustomBaseElement,
  CustomBaseText,
  CustomDescendant,
  SlateBadgeElement,
  SlateEditor,
  SlateEditorAddedProps,
} from '../types/slateTypes'

// pipeline function to modify slate editor default behaviour
export const withReadOnlyBadge = (editor: SlateEditor, addedProps?: SlateEditorAddedProps): SlateEditor => {
  const {
    isInline,
    isElementReadOnly,
    isSelectable,
    deleteBackward,
    deleteForward,
    deleteFragment,
    insertBreak,
    insertSoftBreak,
    insertFragment,
    insertNode,
    insertText,
    normalizeNode,
  } = editor
  const debugMode = addedProps?.debugMode
  let enteredDebug: string | undefined = undefined

  // make badge read-only and inline element
  editor.isInline = (element: CustomBaseElement) => element.type === 'badge' || isInline(element)
  editor.isElementReadOnly = (element: CustomBaseElement) => element.type === 'badge' || isElementReadOnly(element)
  editor.isSelectable = (element: CustomBaseElement) => element.type !== 'badge' && isSelectable(element)

  // custom normalizing nodes logic
  // more info on normalization: https://docs.slatejs.org/concepts/11-normalizing
  editor.normalizeNode = (entry, options) => {
    const [node, path] = entry

    if (Text.isText(node)) {
      // remove multiple spaces in text nodes
      const index = node.text.search(/[\s]{3,}/)
      if (index !== -1) {
        let end = index + 2
        while (node.text.charAt(end) === ' ' && end !== node.text.length - 1) {
          end += 1
        }
        Transforms.delete(editor, { at: { path: path, offset: index }, distance: end - index, unit: 'character' })
        return
      }
    }

    normalizeNode(entry, options)
  }

  editor.deleteBackward = (unit: TextUnit) => {
    const cursor = editor.selection?.anchor
    if (cursor) {
      const startPointToDelete = Editor.before(editor, cursor, { unit: unit })

      // make badge non-deletable
      if (startPointToDelete && isBadgeInRange(editor, startPointToDelete.path, cursor.path)) {
        addedProps?.onBadgeDeletePrevented && addedProps.onBadgeDeletePrevented()
        return
      }

      // restrict delete outside list-item unless insert mode is 'list-item'
      if (
        addedProps?.breakLineInsideList !== 'list-item' &&
        startPointToDelete &&
        cursor.offset === 0 &&
        !areInSameListItem(editor, cursor.path, startPointToDelete.path)
      )
        return
    }
    deleteBackward(unit)
  }

  editor.deleteForward = (unit: TextUnit) => {
    const cursor = editor.selection?.anchor
    if (cursor) {
      const endPointToDelete = Editor.after(editor, cursor, { unit: unit })
      const currentElement = Editor.leaf(editor, cursor)

      // make badge non-deletable
      if (endPointToDelete && isBadgeInRange(editor, cursor.path, endPointToDelete.path)) {
        addedProps?.onBadgeDeletePrevented && addedProps.onBadgeDeletePrevented()
        return
      }

      // restrict delete outside list-item unless insert mode is 'list-item'
      if (
        addedProps?.breakLineInsideList !== 'list-item' &&
        endPointToDelete &&
        cursor.offset === currentElement[0].text.length &&
        !areInSameListItem(editor, cursor.path, endPointToDelete.path)
      )
        return
    }
    deleteForward(unit)
  }

  // make badge non-deletable
  editor.deleteFragment = (options) => {
    isBadgeInSelection(editor)
      ? addedProps?.onBadgeDeletePrevented && addedProps.onBadgeDeletePrevented()
      : deleteFragment(options)
  }

  // reset selection so that badge can't be erased that way
  editor.insertSoftBreak = deselectIfBadgeInSelection(editor, insertSoftBreak)
  editor.insertNode = deselectIfBadgeInSelection(editor, insertNode)
  editor.insertText = deselectIfBadgeInSelection(editor, insertText)
  editor.insertFragment = (fragment, options) => {
    // reset selection if badge is in it to prevent deleting it
    if (isBadgeInSelection(editor)) {
      addedProps?.onBadgeDeletePrevented && addedProps.onBadgeDeletePrevented()
    } else {
      // if we have badges coming from clipboard, extract text and insert that
      // this prevents manual duplication of existing badges
      const fragmentWithoutBadges: Node[] = []
      normalizeFragment(fragment, fragmentWithoutBadges)
      insertFragment(fragmentWithoutBadges, options)
    }
  }

  editor.insertBreak = () => {
    // collapse selection if badge in it so that badge can't be erased that way
    isBadgeInSelection(editor) && Transforms.deselect(editor)

    // if trying to insert break inside list item behave like in additionalProps
    const cursorPath = editor.selection ? Range.start(editor.selection).path : Editor.end(editor, []).path
    if (isInsideListItem(editor, cursorPath)) {
      switch (addedProps?.breakLineInsideList) {
        case 'paragraph':
          insertBreak()
          return
        case 'list-item': {
          // insertBreak() creates new paragraph and then we split at paragraph level again
          const cursorPathRef = Editor.pathRef(editor, cursorPath)
          insertBreak()
          cursorPathRef.current &&
            Transforms.splitNodes(editor, { at: Path.parent(cursorPathRef.current), always: true })
          cursorPathRef.unref()
          return
        }
        // 'prevent' is default so won't add it explicitly
        default:
          return
      }
    }

    insertBreak()
  }

  // =======================> definitions of custom commands for document manipulation <=======================
  editor.removeNodeWithId = (id) => {
    // log if in debug mode
    if (debugMode && !enteredDebug) {
      debugBeforeAction(editor, 'removeNodeWithId(id)', id)
      enteredDebug = 'removeNodeWithId'
    }

    Transforms.removeNodes(editor, { at: [], match: idMatch(id) })

    // need to add empty paragraph if that was a last node (Slate raises exception if editor.children = [])
    if (editor.children.length === 0) {
      Transforms.insertNodes(editor, { type: 'paragraph', children: [{ text: '' }] } as CustomDescendant, { at: [0] })
    }

    // log if in debug mode
    if (debugMode && enteredDebug === 'removeNodeWithId') {
      debugAfterAction(editor)
      enteredDebug = undefined
    }
  }

  editor.pushNode = (node, options) => {
    // log if in debug mode
    if (debugMode && !enteredDebug) {
      debugBeforeAction(editor, 'pushNode(node, options)', node, options)
      enteredDebug = 'pushNode'
    }

    const wasBlank = slateUtils.isBlank(editor.children)
    let locationToInsert = [editor.children.length]

    if (options) {
      const otherNode =
        options.otherNodeId && Editor.nodes(editor, { at: [], match: idMatch(options.otherNodeId) }).next()?.value
      if (otherNode)
        switch (options.position) {
          case 'after':
            locationToInsert = Path.next(otherNode[1])
            break
          case 'before':
            // if 'before' we wan't to insert new node to otherNode place (and other node will be 'moved' forward)
            locationToInsert = otherNode[1]
            break
          case 'firstChild':
            locationToInsert = [...otherNode[1], 0] as Path
            break
        }
      else if (options.position === 'start') {
        locationToInsert = [0]
      } else console.error('[Slate Editor: pushNode()]: otherNode is null and position is not "start"!')
    }
    Transforms.insertNodes(editor, node, { at: locationToInsert })

    // remove other node(s) if it's only blanks
    if (wasBlank) {
      Transforms.removeNodes(editor, {
        at: [],
        match: (node, path) => !Path.isCommon(path, locationToInsert) && !Path.isDescendant(path, locationToInsert),
        mode: 'highest',
      })
    }

    // log if in debug mode
    if (debugMode && enteredDebug === 'pushNode') {
      debugAfterAction(editor)
      enteredDebug = undefined
    }
  }

  editor.updateBadgeValue = (badgeId, newValue, parentPath) => {
    // log if in debug mode
    if (debugMode && !enteredDebug) {
      debugBeforeAction(editor, 'updateBadgeValue(badgeId, newValue, parentPath)', badgeId, newValue, parentPath)
      enteredDebug = 'updateBadgeValue'
    }

    const badges = Array.from(
      Editor.nodes(editor, {
        at: [],
        match: parentPath ? idAndParentPathMatch(badgeId, parentPath) : idMatch(badgeId),
      }),
    )

    badges.forEach(([badge, badgePath]) => {
      const updatedBadge = { ...badge } as SlateBadgeElement
      updatedBadge.children = newValue
      const pathRef = Editor.pathRef(editor, badgePath) // pathRef will stay in sync as new operations are applied to the editor.
      Transforms.insertNodes(editor, updatedBadge, { at: badgePath })
      pathRef.current
        ? Transforms.delete(editor, { at: pathRef.current })
        : console.error('[Slate Editor: updateBadgeValue()]: pathRef is null!')
      pathRef.unref()
    })

    // log if in debug mode
    if (debugMode && enteredDebug === 'updateBadgeValue') {
      debugAfterAction(editor)
      enteredDebug = undefined
    }
  }

  editor.updateBadgesinNode = (nodeId, badges) => {
    // log if in debug mode
    if (debugMode && !enteredDebug) {
      debugBeforeAction(editor, 'updateBadgesinNode(nodeId, badges)', nodeId, badges)
      enteredDebug = 'updateBadgesinNode'
    }

    // find lowest text node below provided nodeId
    const rootPath = Editor.nodes(editor, { at: [], match: idMatch(nodeId) }).next().value?.[1]
    const firstSiblingPath = Path.isPath(rootPath) ? (Editor.first(editor, rootPath)?.at(1) as Path) : undefined
    if (!firstSiblingPath) {
      console.error('[Slate Editor: updateBadgesinNode()]: firstSiblingPath is null!')
      return
    }
    // get all existing badges and make pathRefs that are in sync as new operations are applied to the editor
    const existingBadgesBeforeDelete = Array.from(
      Editor.nodes(editor, {
        at: [],
        match: (node, path) => Path.isSibling(firstSiblingPath, path) && (node as CustomBaseElement).type === 'badge',
      }),
    ).map(([n, path]) => ({ node: n as SlateBadgeElement, pathRef: Editor.pathRef(editor, path) }))

    const existingBadges: {
      node: SlateBadgeElement
      pathRef: PathRef
    }[] = []
    // delete nodes that are not in provided array (badges)
    existingBadgesBeforeDelete.forEach((item, i) => {
      // if existing is not in new array delete it
      if (!badges.some((badge) => badge.id === item.node.id)) {
        if (!item.pathRef.current) {
          console.error('[Slate Editor: updateBadgesinNode()]: pathRef.current is null!')
          return
        }

        const prevText = Path.hasPrevious(item.pathRef.current)
          ? Editor.leaf(editor, Path.previous(item.pathRef.current))
          : undefined
        // if previous Text ends with ",", delete it
        if (prevText?.[0].text.trim().endsWith(',')) {
          const commaIndex = prevText[0].text.lastIndexOf(',')
          Transforms.delete(editor, { at: { path: prevText[1], offset: commaIndex }, distance: 1, unit: 'character' })
        }
        // if previous text ends with "-" and this is last badge, delete the separator
        if (i === existingBadgesBeforeDelete.length - 1 && prevText?.[0].text.trim().endsWith('-')) {
          const separatorIndex = prevText[0].text.lastIndexOf('-')
          Transforms.delete(editor, {
            at: { path: prevText[1], offset: separatorIndex },
            distance: 1,
            unit: 'character',
          })
        }

        // if we are deleting a first badge, also delete "," after it if present
        if (i === 0) {
          const nextText = Editor.hasPath(editor, Path.next(item.pathRef.current))
            ? Editor.leaf(editor, Path.next(item.pathRef.current))
            : undefined
          // check if the first character is ","
          if (nextText?.[0].text.trim().startsWith(',')) {
            const text = nextText[0].text
            // find last space after the comma
            const commaIndex = text.indexOf(',')
            let lastSpaceIndex = commaIndex
            while (text.at(lastSpaceIndex + 1) === ' ') {
              lastSpaceIndex += 1
            }

            // delete text starting with comma and ending with the last space
            Transforms.delete(editor, {
              at: { path: nextText[1], offset: commaIndex },
              distance: lastSpaceIndex - commaIndex + 1,
              unit: 'character',
            })
          }
        }

        // delete badge itself
        Transforms.removeNodes(editor, { at: item.pathRef.current })
        item.pathRef.unref()
      }
      // else push it in new array so it can be updated
      else {
        existingBadges.push(item)
      }
    })

    // sort existing by path
    existingBadges.sort(({ pathRef: pathRefA }, { pathRef: pathRefB }) =>
      pathRefA.current && pathRefB.current ? (Path.isBefore(pathRefA.current, pathRefB.current) ? -1 : 1) : 0,
    )
    // update existing and create new badges
    badges.forEach((badge, i) => {
      const existing = existingBadges.find((obj) => obj.node.id === badge.id)

      // it's existing, update it
      if (existing) {
        const pathCopy = existing.pathRef.current
        editor.updateBadgeValue(badge.id, badge.children, rootPath)
        // recreate pathRef because updating deletes actual node in the background and pathRef becomes null
        existing.pathRef.unref()
        if (pathCopy) {
          existing.pathRef = Editor.pathRef(editor, pathCopy)
        } else {
          console.error("'[Slate Editor: updateBadgesinNode()]: pathCopy is null!'")
        }
      }
      // it's new, insert after existing sibling
      else {
        const prevSibling = i !== 0 ? badges[i - 1] : undefined
        const existingPrevSibling = existingBadges.find((node) => node.node.id === prevSibling?.id)

        // insert after existingPrevSibling or at start if existingPrevSibling undefined
        const pathToInsert = Path.next(existingPrevSibling?.pathRef.current ?? firstSiblingPath)

        // if this is a first badge -> don't insert separator before it
        // if separator prop is set -> insert separator from prop before it
        // else -> insert default separator ", " before it
        const nodesToInsert: Node[] = []
        // if NOT a first node
        if (!Path.equals(Path.previous(pathToInsert), firstSiblingPath)) {
          // insert from prop or default
          nodesToInsert.push(badge.separator ?? { text: ', ' })
        }
        // add badge itself and empty text after (required by Slate.js to avoid weird behaviour)
        nodesToInsert.push(badge, { text: '' })

        // EDGE CASE: Inserting first badge in paragraph and the next one already exists
        //            In that case, separator doesn't get inserted, so will be added here
        const nextSibling = badges.at(i + 1)
        if (i === 0 && nextSibling && existingBadges.some((node) => node.node.id === nextSibling.id)) {
          nodesToInsert.push(nextSibling.separator ?? { text: ', ' })
        }

        Transforms.insertNodes(editor, nodesToInsert, { at: pathToInsert })
        // push just created node to existingBadges
        existingBadges.push({ node: badge, pathRef: Editor.pathRef(editor, Path.next(pathToInsert)) }) // Path.next because there's always text after badge
      }
    })
    // free all refs
    existingBadges.forEach((i) => i.pathRef.unref())

    // log if in debug mode
    if (debugMode && enteredDebug === 'updateBadgesinNode') {
      debugAfterAction(editor)
      enteredDebug = undefined
    }
  }

  editor.updateTagForNode = (nodeId, newValue) => {
    // log if in debug mode
    if (debugMode && !enteredDebug) {
      debugBeforeAction(editor, 'updateTagForNode(nodeId, newValue)', nodeId, newValue)
      enteredDebug = 'updateTagForNode'
    }

    newValue
      ? Transforms.setNodes(editor, { tag: newValue } as Partial<CustomDescendant>, {
          at: [],
          match: idMatch(nodeId),
          mode: 'all',
        })
      : Transforms.unsetNodes(editor, ['tag'], { at: [], match: idMatch(nodeId), mode: 'all' })

    // log if in debug mode
    if (debugMode && enteredDebug === 'updateTagForNode') {
      debugAfterAction(editor)
      enteredDebug = undefined
    }
  }

  editor.updateNode = (nodeId, index) => {
    // log if in debug mode
    if (debugMode && !enteredDebug) {
      debugBeforeAction(editor, 'updateNode(nodeId, index)', nodeId, index)
      enteredDebug = 'updateNode'
    }

    //remove node with higest id
    Transforms.removeNodes(editor, { at: [index] })
    //insert node with missing id
    Transforms.insertNodes(editor, nodeId, { at: [index] })

    // log if in debug mode
    if (debugMode && enteredDebug === 'updateNode') {
      debugAfterAction(editor)
      enteredDebug = undefined
    }
  }

  editor.isBlank = () => slateUtils.isBlank(editor.children)

  editor.nodeExists = (id) => Editor.nodes(editor, { at: [], match: idMatch(id) }).next().value !== undefined

  editor.getSiblingsOfNode = (nodeId) => {
    const rootPath = Editor.nodes(editor, { at: [], match: idMatch(nodeId) }).next().value?.[1]
    if (!rootPath) {
      console.error('[Slate Editor: getSiblingsOfNode(nodeId)]: rootPath is null!')
      return []
    }
    return [...(Editor.nodes(editor, { at: [], match: (node, path) => Path.isSibling(path, rootPath) }) ?? [])].map(
      (n) => n[0],
    )
  }

  editor.getChildrenOfNode = (nodeId) => {
    const rootPath = Editor.nodes(editor, { at: [], match: idMatch(nodeId) }).next().value?.[1]
    if (!rootPath) {
      console.error('[Slate Editor: getChildrenOfNode(nodeId)]: rootPath is null!')
      return []
    }
    return [...(Editor.nodes(editor, { at: [], match: (node, path) => Path.isChild(path, rootPath) }) ?? [])].map(
      (n) => n[0],
    )
  }

  return editor
}

const idMatch = (id: string) => {
  return (node: Node) => !Editor.isEditor(node) && Element.isElement(node) && (node as CustomBaseElement).id === id
}

const idAndParentPathMatch = (id: string, parentPath: Path) => {
  return (node: Node, path: Path) =>
    !Editor.isEditor(node) &&
    Element.isElement(node) &&
    (node as CustomBaseElement).id === id &&
    Path.isDescendant(path, parentPath)
}

const isInsideListItem = (editor: SlateEditor, path: Path) =>
  !!Editor.above(editor, {
    at: path,
    match: (node) => (node as CustomBaseElement).type === 'list-item',
  })

const areInSameListItem = (editor: SlateEditor, path: Path, other: Path) => {
  if (Path.equals(path, other)) return true

  const pointListItem = Editor.above(editor, {
    at: path,
    match: (node) => (node as CustomBaseElement).type === 'list-item',
  })
  if (pointListItem) {
    return Path.isAncestor(pointListItem[1], other)
  } else {
    return !Editor.above(editor, { at: other, match: (node) => (node as CustomBaseElement).type === 'list-item' })
  }
}

const isBadgeInSelection = (editor: SlateEditor) => {
  if (editor.selection) {
    const [selectionStart, selectionEnd] = Range.edges(editor.selection)
    return isBadgeInRange(editor, selectionStart.path, selectionEnd.path)
  }
  return false
}

const isBadgeInRange = (editor: SlateEditor, rangeStart: Path, rangeEnd: Path) => {
  let badgeIsInRange = false
  const badges = Array.from(Editor.nodes(editor, { at: [], match: (n) => (n as CustomBaseElement).type === 'badge' }))

  badges.forEach((node) => {
    const badgePath = node[1]
    // if badge is after selection start AND before selection end, abort fragment delete
    if (Path.compare(badgePath, rangeStart) !== -1 && Path.compare(badgePath, rangeEnd) !== 1) {
      badgeIsInRange = true
      return
    }
  })
  return badgeIsInRange
}

// eslint-disable-next-line @typescript-eslint/ban-types
const deselectIfBadgeInSelection = (editor: SlateEditor, originalFunction: Function) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (...args: any[]) => {
    isBadgeInSelection(editor) && Transforms.deselect(editor)
    return originalFunction(...args)
  }
}

// modify fragment in a way to extract text from badges and remove badge nodes
// it also extracts content from list-item and removes list-item nodes
const normalizeFragment = (originalFragment: Node[], changedFragment: Node[]) => {
  originalFragment.forEach((node) => {
    if ((node as BaseElement).children) {
      const nodeType = (node as CustomBaseElement).type
      // if badge or list-item skip it and add normalized children
      if (nodeType && ['badge', 'list-item'].includes(nodeType)) {
        normalizeFragment((node as BaseElement).children, changedFragment)
        return
      }

      // else take the element and add it, also add normalized children
      const changedNode = { ...node, children: [], id: undefined } // unset id not to make duplicates
      normalizeFragment((node as BaseElement).children, changedNode.children)
      changedFragment.push(changedNode)
      return
    }
    // if text, just take it as it is
    changedFragment.push(node)
  })
}

// helper functions to log custom actions for easier debugging
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const debugBeforeAction = (editor: SlateEditor, actionName: string, ...args: any[]) => {
  console.log('\n---------------[SlateEditor] Remove prop debugMode to stop this logs------------------')
  console.log(`${actionName}\n`)
  console.log('arguments:')
  args.forEach((arg) => console.log(arg))
  console.log('\neditor state before action:')
  console.log(JSON.parse(JSON.stringify(editor.children))) // doing deep-clone: https://developer.mozilla.org/en-US/docs/Web/API/Console/log#logging_objects
}
const debugAfterAction = (editor: SlateEditor) => {
  console.log('\neditor state after action:')
  console.log(JSON.parse(JSON.stringify(editor.children)))
  console.log('---------------------------------------------\n')
}

export const slateUtils = {
  /**
   * Returns true if editor has only blanks in every text node
   */
  isBlank: (slateList?: CustomDescendant[]): boolean => {
    if (!slateList || slateList.length === 0) return true
    return slateList.every((node) => {
      const text = (node as CustomBaseText).text
      if (text) {
        return text.trim() === ''
      }
      return slateUtils.isBlank((node as CustomBaseElement).children)
    })
  },

  /**
   * Converts SlateJS value list to plain text
   * - Extracts text from badges
   * - list-item's dash is mapped as #lI$
   * - paragraph is mapped as \n
   * - bold style is mapped with #b$ wrapped around bolded text
   */
  toPlainText: (slateList?: CustomDescendant[]): string => {
    return slateUtils.toPlainTextInner(slateList).output
  },

  /**
   * Used internally by toPlainText for recursive calls
   */
  toPlainTextInner: (slateList?: CustomDescendant[], insideListItem?: boolean, prevWasBold?: boolean) => {
    let output = ''
    let innerPrevWasBold = prevWasBold

    slateList?.forEach((node, i) => {
      const textNode = node as CustomBaseText
      if (textNode.text) {
        // if current bold and prev are different (basically logical XOR) add bold char
        if (!textNode.bold !== !innerPrevWasBold) output += '#b$'

        output += textNode.text
        innerPrevWasBold = textNode.bold
      } else {
        const element = node as CustomBaseElement
        if (element.type === 'list-item') {
          output += '#lI$'
        }

        const deeperRes = slateUtils.toPlainTextInner(element.children, element.type === 'list-item', innerPrevWasBold)
        output += deeperRes.output
        innerPrevWasBold = deeperRes.prevWasBold
        //edge case when last text node is bold
        if (innerPrevWasBold && i === slateList.length - 1) {
          // add bold char to output
          output += '#b$'
          // reset the bold flag that is returned to prev iteration to prevent bold char duplication
          innerPrevWasBold = false
        }

        //don't add \n after the last paragraph inside list-item
        if (element.type === 'paragraph' && ((insideListItem && i !== slateList.length - 1) || !insideListItem)) {
          output += '\n'
        }
      }
    })

    return { output, prevWasBold: innerPrevWasBold }
  },
}
